import React, { useEffect, useState } from 'react';
import Dialog from './TivigoDialog';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import './userPanelSecurityModal.css';

const FILTERS = [
  { id: 'success', label: 'Başarılı Girişler' },
  { id: 'failed', label: 'Başarısız Girişler' },
  { id: 'password_reset', label: 'Şifre Sıfırlama' },
];

const PREVIEW_LOGS = {
  success: [
    {
      id: 'preview-success-1',
      event_type: 'login_attempt',
      description: 'Başarılı giriş yapıldı',
      ip_address: '185.•••.•••.24',
      browser_info: 'Chrome · Windows 11',
      success: 1,
      timestamp: Math.floor(Date.now() / 1000) - 840,
    },
    {
      id: 'preview-success-2',
      event_type: 'login_attempt',
      description: 'Başarılı giriş yapıldı',
      ip_address: '88.•••.•••.16',
      browser_info: 'Tivigo Windows',
      success: 1,
      timestamp: Math.floor(Date.now() / 1000) - 86400,
    },
  ],
  failed: [
    {
      id: 'preview-failed-1',
      event_type: 'login_attempt',
      description: 'Başarısız giriş denemesi',
      ip_address: '46.•••.•••.91',
      browser_info: 'Bilinmeyen cihaz',
      success: 0,
      timestamp: Math.floor(Date.now() / 1000) - 12600,
    },
  ],
  password_reset: [
    {
      id: 'preview-reset-1',
      event_type: 'password_reset',
      description: 'Şifre sıfırlama başarılı',
      ip_address: '185.•••.•••.24',
      browser_info: 'Chrome · Windows 11',
      success: 1,
      timestamp: Math.floor(Date.now() / 1000) - 172800,
    },
  ],
};

const formatSecurityDate = (timestamp) => {
  const numeric = Number(timestamp);
  const date = Number.isFinite(numeric)
    ? new Date(numeric > 100000000000 ? numeric : numeric * 1000)
    : new Date(timestamp);

  if (Number.isNaN(date.getTime())) return 'Tarih bilgisi yok';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

function UserPanelSecurityModal({
  open,
  onClose,
  userInfo,
  previewMode = false,
  onFetchLogs,
}) {
  const [filter, setFilter] = useState('success');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const targetUserId = userInfo?._targetUserId || userInfo?.target_user_id || null;
  const targetUsername = String(
    userInfo?.user_info?.username || userInfo?.user?.username || ''
  ).trim();

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');

    const request = previewMode
      ? Promise.resolve({
          logs: PREVIEW_LOGS[filter] || [],
          pagination: { page: 1, pages: 1, total: (PREVIEW_LOGS[filter] || []).length },
        })
      : typeof onFetchLogs === 'function'
        ? Promise.resolve().then(() => onFetchLogs({ userInfo, filter, page, limit: 20 }))
        : Promise.reject(new Error('Güvenlik kayıtları bağlantısı sağlanmadı.'));

    request
      .then((result) => {
        if (cancelled) return;
        const nextLogs = Array.isArray(result?.logs) ? result.logs : [];
        const nextPagination = result?.pagination || {};
        setLogs(nextLogs);
        setPagination({
          page: Number(nextPagination.page) || page,
          pages: Math.max(1, Number(nextPagination.pages) || 1),
          total: Number(nextPagination.total) || nextLogs.length,
        });
      })
      .catch((requestError) => {
        if (!cancelled) {
          setLogs([]);
          setPagination({ page: 1, pages: 1, total: 0 });
          setError(requestError?.message || 'Güvenlik kayıtları yüklenemedi.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filter, onFetchLogs, open, page, previewMode, userInfo]);

  useEffect(() => {
    if (open) return;
    setFilter('success');
    setPage(1);
  }, [open]);

  const activeFilterIndex = Math.max(0, FILTERS.findIndex((item) => item.id === filter));
  const filterTabIndicatorX = [
    '0px',
    'calc(100% + 5px)',
    'calc(200% + 10px)',
  ][activeFilterIndex];

  const selectFilter = (nextFilter) => {
    setFilter(nextFilter);
    setPage(1);
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      keepMounted
      transitionDuration={0}
      disableEscapeKeyDown
      className="tvup-security-dialog"
      aria-labelledby="tvup-security-modal-title"
      slotProps={{
        backdrop: { className: 'tvup-security-backdrop' },
        paper: { id: 'tvup-security-modal', className: 'tvup-security-modal' },
      }}
    >
      <header className="tvup-security-header">
        <span className="tvup-security-title-icon" aria-hidden="true"><SecurityRoundedIcon /></span>
        <div>
          <h2 id="tvup-security-modal-title">Güvenlik Kayıtları</h2>
          <p>
            {targetUserId ? (
              <><b>{targetUsername || 'Seçilen alt kullanıcı'}</b> hesabına ait giriş ve şifre işlemleri.</>
            ) : (
              'Hesabınızdaki giriş ve şifre işlemlerini inceleyin.'
            )}
          </p>
        </div>
        <button type="button" aria-label="Güvenlik kayıtlarını kapat" onClick={onClose}>
          <CloseRoundedIcon />
        </button>
      </header>

      <nav
        className="tvup-security-filters"
        role="tablist"
        aria-label="Güvenlik kayıt türleri"
        style={{ '--tvup-security-tab-x': filterTabIndicatorX }}
      >
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            className={filter === item.id ? 'is-active' : ''}
            aria-selected={filter === item.id}
            onClick={() => selectFilter(item.id)}
          >
            {item.id === 'password_reset' ? <KeyRoundedIcon /> : item.id === 'success' ? <CheckCircleRoundedIcon /> : <ErrorOutlineRoundedIcon />}
            {item.label}
          </button>
        ))}
      </nav>

      <section className="tvup-security-content" aria-live="polite">
        {loading ? (
          <div className="tvup-security-state"><CircularProgress size={24} thickness={5} /><b>Kayıtlar yükleniyor</b></div>
        ) : error ? (
          <div className="tvup-security-state is-error"><ErrorOutlineRoundedIcon /><b>{error}</b></div>
        ) : logs.length === 0 ? (
          <div className="tvup-security-state"><SecurityRoundedIcon /><b>Bu filtrede kayıt bulunmuyor.</b></div>
        ) : (
          <div className="tvup-security-log-list">
            {logs.map((log, index) => {
              const succeeded = Number(log.success) === 1;
              return (
                <article key={log.id || `${filter}-${index}`} className={succeeded ? 'is-success' : 'is-failed'}>
                  <span className="tvup-security-log-icon">
                    {log.event_type === 'password_reset' ? <KeyRoundedIcon /> : succeeded ? <CheckCircleRoundedIcon /> : <ErrorOutlineRoundedIcon />}
                  </span>
                  <div className="tvup-security-log-copy">
                    <b>{log.description || (succeeded ? 'Başarılı güvenlik işlemi' : 'Başarısız güvenlik işlemi')}</b>
                    <span>{log.browser_info || log.user_agent || 'Cihaz bilgisi yok'}</span>
                  </div>
                  <div className="tvup-security-log-meta">
                    <b>{log.ip_address || 'IP bilgisi yok'}</b>
                    <span>{formatSecurityDate(log.timestamp || log.ts || log.login_at || log.attempted_at)}</span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <footer className="tvup-security-footer">
        <button type="button" aria-label="Önceki sayfa" disabled={loading || page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
          <NavigateBeforeRoundedIcon /> Önceki
        </button>
        <span>{pagination.page} / {pagination.pages}</span>
        <button type="button" aria-label="Sonraki sayfa" disabled={loading || page >= pagination.pages} onClick={() => setPage((current) => current + 1)}>
          Sonraki <NavigateNextRoundedIcon />
        </button>
      </footer>
    </Dialog>
  );
}

export default UserPanelSecurityModal;
